/**
 * Utilidades para formatear números en formato de moneda costarricense
 */

/**
 * Formatea un número como moneda costarricense (₡) con comas cada tres dígitos y dos decimales
 * @param value - El valor numérico a formatear
 * @returns String formateado como moneda costarricense
 * @example
 * formatCostaRicanCurrency(1234567.89) // "₡1,234,567.89"
 * formatCostaRicanCurrency(1000) // "₡1,000.00"
 * formatCostaRicanCurrency(0) // "₡0.00"
 */
export const formatCostaRicanCurrency = (value: number | string): string => {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numericValue) || !isFinite(numericValue)) {
    return '₡0.00';
  }

  return new Intl.NumberFormat('es-CR', {
    style: 'currency',
    currency: 'CRC',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true, // Asegura que se muestren los separadores de miles
  }).format(numericValue);
};

/**
 * Formatea un número con comas cada tres dígitos y dos decimales (sin símbolo de moneda)
 * @param value - El valor numérico a formatear
 * @returns String formateado con comas y decimales
 * @example
 * formatNumberWithCommas(1234567.89) // "1,234,567.89"
 * formatNumberWithCommas(1000) // "1,000.00"
 */
export const formatNumberWithCommas = (value: number | string): string => {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numericValue) || !isFinite(numericValue)) {
    return '0.00';
  }

  return new Intl.NumberFormat('es-CR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true, // Asegura que se muestren los separadores de miles
  }).format(numericValue);
};

/**
 * Formatea un número como porcentaje con dos decimales
 * @param value - El valor numérico a formatear
 * @returns String formateado como porcentaje
 * @example
 * formatPercentage(19.5) // "19.50%"
 * formatPercentage(13) // "13.00%"
 */
export const formatPercentage = (value: number | string): string => {
  const numericValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numericValue) || !isFinite(numericValue)) {
    return '0.00%';
  }

  return `${numericValue.toFixed(2)}%`;
};

/**
 * Convierte un string formateado de vuelta a número
 * @param formattedValue - String formateado (ej: "₡1,234.56" o "1,234.56")
 * @returns Número extraído del string
 * @example
 * parseFormattedNumber("₡1,234.56") // 1234.56
 * parseFormattedNumber("1,234.56") // 1234.56
 */
export const parseFormattedNumber = (formattedValue: string): number => {
  if (!formattedValue) return 0;

  // Remover símbolos de moneda y espacios
  const cleanValue = formattedValue.replace(/[₡$,\s]/g, '');
  const numericValue = parseFloat(cleanValue);

  return isNaN(numericValue) ? 0 : numericValue;
};

/**
 * Convierte horas a UT Audatex (1 hora = 10 UT)
 * @param hours - Número de horas a convertir
 * @returns Número de UT Audatex
 * @example
 * convertHoursToUT(1) // 10
 * convertHoursToUT(2.5) // 25
 * convertHoursToUT(0) // 0
 */
export const convertHoursToUT = (hours: number): number => {
  const numericHours = typeof hours === 'string' ? parseFloat(hours) : hours;

  if (isNaN(numericHours) || !isFinite(numericHours)) {
    return 0;
  }

  return Math.round(numericHours * 10);
};
